import { useMemo, useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import { api, type OntologyStats } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { Icon } from "../icons";
import { EDGES, OBJECTS, VIA_LABEL, objectById, type Edge } from "../ontology";
import { STATUS_LABEL, findArea } from "../suite";
import { ACTIONS, AREA_OF, FUNCTIONS, INTERFACES, PROPS, QUESTIONS, cardOf, linksOf, sourceOf } from "../ontologySchema";

const fmt = (n: number) => new Intl.NumberFormat("en").format(n);
const live = (s: string) => s === "live";
export const typeHref = (id: string) => `/ontology/t/${id}`;

export function TypeChip({ id }: { id: string }) {
  const o = objectById(id);
  if (!o) return <span>{id}</span>;
  return (
    <Link to={typeHref(id)} className={`node ${o.side} ${live(o.status) ? "" : "planned"}`} style={{ height: 26, fontSize: 12.5 }}>
      {o.side === "bc" ? <span className="tag bc">BC</span> : <span className="tag cm" aria-hidden="true" />}
      <span className="nm">{o.name}</span>
    </Link>
  );
}

/* ---------- The map: object types left (courageMFG) and right (Business Central) ---------- */

const W = 1040, ROW = 46, TOP = 50, NODE_W = 220, NODE_H = 34, LX = 70, RX = W - 70 - NODE_W;

function OntologyMap({ stats }: { stats?: OntologyStats | null }) {
  const nav = useNavigate();
  const [sel, setSel] = useState<string>("lot");
  const [area, setArea] = useState("quality");
  // An area shows its own object types plus everything they link to directly.
  const shown = useMemo(() => {
    if (!area) return new Set(OBJECTS.map((o) => o.id));
    const own = OBJECTS.filter((o) => AREA_OF[o.id] === area).map((o) => o.id);
    const set = new Set(own);
    EDGES.forEach((e) => { if (own.includes(e.from)) set.add(e.to); if (own.includes(e.to)) set.add(e.from); });
    return set;
  }, [area]);
  const left = OBJECTS.filter((o) => o.side === "courage" && shown.has(o.id));
  const right = OBJECTS.filter((o) => o.side === "bc" && shown.has(o.id));
  const edges = EDGES.filter((e) => shown.has(e.from) && shown.has(e.to));
  const H = TOP + Math.max(left.length, right.length, 1) * ROW + 16;
  const pos = useMemo(() => {
    const p: Record<string, { x: number; y: number; side: "courage" | "bc" }> = {};
    left.forEach((o, i) => (p[o.id] = { x: LX, y: TOP + i * ROW, side: "courage" }));
    right.forEach((o, i) => (p[o.id] = { x: RX, y: TOP + i * ROW, side: "bc" }));
    return p;
  }, [shown]);
  const areas = [...new Set(Object.values(AREA_OF))].map((id) => findArea(id)).filter(Boolean);
  const pick = (a: string) => {
    setArea(a);
    const own = OBJECTS.find((o) => AREA_OF[o.id] === a && o.status === "live") ?? OBJECTS.find((o) => AREA_OF[o.id] === a);
    if (a && own) setSel(own.id);
  };
  const recordCount = (id: string) => stats?.records.find((r) => r.type === id)?.count;
  const linkStat = (id: string) => stats?.links.find((l) => l.entity === id);
  const path = (e: Edge) => {
    const a = pos[e.from], b = pos[e.to];
    const ay = a.y + NODE_H / 2, by = b.y + NODE_H / 2;
    if (a.side !== b.side) {
      const x1 = a.side === "courage" ? a.x + NODE_W : a.x, x2 = b.side === "bc" ? b.x : b.x + NODE_W;
      const mx = (x1 + x2) / 2;
      return { d: `M${x1},${ay} C${mx},${ay} ${mx},${by} ${x2},${by}`, lx: mx, ly: (ay + by) / 2 };
    }
    const x = a.side === "courage" ? a.x : a.x + NODE_W;
    const bulge = (a.side === "courage" ? -1 : 1) * (28 + Math.abs(by - ay) * 0.18);
    return { d: `M${x},${ay} C${x + bulge},${ay} ${x + bulge},${by} ${x},${by}`, lx: x + bulge * 0.75, ly: (ay + by) / 2 };
  };
  const selEdges = edges.filter((e) => e.from === sel || e.to === sel);
  const so = objectById(sel);

  return (
    <div className="dash-card">
      <div className="card-head" style={{ padding: 0, border: 0, marginBottom: 6 }}>
        <div><div className="dash-card-h">Object types and link types</div><div className="dash-cap">Pick an area, select a type to see its links, double-click to open it.</div></div>
        <select className="select" style={{ width: 200 }} aria-label="Area" value={area} onChange={(e) => pick(e.target.value)}>
          <option value="">All areas</option>
          {areas.map((a) => <option key={a!.id} value={a!.id}>{a!.name}</option>)}
        </select>
        <div className="onto-legend">
          <span><svg width="26" height="6" aria-hidden="true"><line x1="0" y1="3" x2="26" y2="3" stroke="#0A7C8C" strokeWidth="2" /></svg>Live</span>
          <span><svg width="26" height="6" aria-hidden="true"><line x1="0" y1="3" x2="26" y2="3" stroke="#8A8A8A" strokeWidth="2" strokeDasharray="4 3" /></svg>Planned</span>
        </div>
      </div>
      <svg className="onto-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="courageMFG object types on the left linked to Business Central object types on the right">
        <text x={LX} y={28} fontSize="12" fontWeight="600" fill="#616161">COURAGEMFG OBJECTS</text>
        <text x={RX} y={28} fontSize="12" fontWeight="600" fill="#616161">BUSINESS CENTRAL OBJECTS</text>
        {edges.map((e, i) => {
          const p = path(e); const on = e.from === sel || e.to === sel; const lv = live(e.status);
          return (
            <g key={i} opacity={on ? 1 : 0.1}>
              <path d={p.d} fill="none" stroke={lv ? "#0A7C8C" : "#8A8A8A"} strokeWidth={on ? 2 : 1.3} strokeDasharray={lv ? undefined : "5 4"} />
              {on && <text x={p.lx} y={p.ly - 4} fontSize="11" textAnchor="middle" fill="#424242" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 4 }}>{e.label}</text>}
            </g>
          );
        })}
        {[...left, ...right].map((o) => {
          const p = pos[o.id]; const active = o.id === sel;
          const connected = selEdges.some((e) => e.from === o.id || e.to === o.id);
          const n = o.side === "courage" ? recordCount(o.id) : linkStat(o.id)?.distinctRecords;
          const planned = !live(o.status);
          return (
            <g key={o.id} transform={`translate(${p.x},${p.y})`} style={{ cursor: "pointer" }} onClick={() => setSel(o.id)} onDoubleClick={() => nav(typeHref(o.id))} opacity={active || connected ? 1 : 0.55}
              role="button" aria-label={`${o.name}${n ? `, ${n}` : ""}`} tabIndex={0} onKeyDown={(ev) => { if (ev.key === " ") setSel(o.id); if (ev.key === "Enter") nav(typeHref(o.id)); }}>
              <rect width={NODE_W} height={NODE_H} rx="4" fill={active ? (o.side === "bc" ? "#1E2938" : "#0A7C8C") : "#FFFFFF"} stroke={active ? "none" : planned ? "#D1D1D1" : o.side === "bc" ? "#1E2938" : "#0A7C8C"} strokeWidth="1.2" strokeDasharray={planned && !active ? "4 3" : undefined} />
              <text x="12" y="22" fontSize="13" fontWeight="600" fill={active ? "#FFFFFF" : planned ? "#8A8A8A" : "#242424"}>{o.name}</text>
              {!!n && <text x={NODE_W - 12} y="22" fontSize="12" textAnchor="end" fill={active ? "#E6F4F6" : "#616161"}>{n}</text>}
            </g>
          );
        })}
      </svg>
      <div className="onto-sel">
        <TypeChip id={sel} />
        <span className="muted">{so.what}</span>
        <Link to={typeHref(sel)} className="btn" style={{ marginLeft: "auto" }}>Open object type</Link>
      </div>
    </div>
  );
}

/* ---------- Tabs ---------- */

function Overview({ stats }: { stats?: OntologyStats | null }) {
  const objects = stats?.records.reduce((s, r) => s + r.count, 0);
  const bcLinked = stats?.links.reduce((s, l) => s + l.distinctRecords, 0);
  const fig = (label: string, value: number | undefined, cap: string, src: string) => (
    <div className="dash-card dash-figure">
      <div className="dash-label">{label}</div>
      <div className={`dash-num ${value === undefined ? "dash-skeleton" : ""}`}>{value === undefined ? "" : fmt(value)}</div>
      <div className="dash-cap">{cap}</div>
      <div className="dash-src"><span className="dot" />{src}</div>
    </div>
  );
  return (
    <>
      <div className="dash-figures" style={{ marginBottom: 12, gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 9.5rem), 1fr))" }}>
        {fig("Object types", OBJECTS.length, `${OBJECTS.filter((o) => live(o.status)).length} live`, "Ontology definition")}
        {fig("Link types", EDGES.length, `${EDGES.filter((e) => live(e.status)).length} live`, "Ontology definition")}
        {fig("Action types", ACTIONS.length, `${ACTIONS.filter((a) => a.mcp).length} callable by AI today`, "Ontology definition")}
        {fig("courageMFG objects", objects, "Issues and CAPAs, all time", "courageMFG")}
        {fig("BC records linked", bcLinked, "Distinct lots, items, vendors…", "courageMFG links")}
      </div>
      <p className="dash-scope" style={{ marginBottom: 16 }}>
        <b>Semantic layer:</b> objects, their properties and the links between them. <b>Kinetic layer:</b> the actions that change them and the functions that reason over them. Both are what Claude and Copilot use through the courageMFG connector.
      </p>
      <OntologyMap stats={stats} />
      <section className="area-sec" style={{ marginTop: 24 }}>
        <div className="sec-h"><h2>Questions the linked model answers</h2><span className="muted">Each one walks a path through the ontology. Ask them in Claude or Copilot.</span></div>
        <div className="dash-card" style={{ padding: 0 }}>
          <table className="dgrid">
            <thead><tr><th>Question</th><th>Path</th><th>Status</th></tr></thead>
            <tbody>
              {QUESTIONS.map((q) => (
                <tr key={q.q}>
                  <td style={{ fontWeight: 600 }}>{q.q}</td>
                  <td><span className="path">{q.path.map((p, i) => <span key={p + i}>{i > 0 && <Icon name="chev" size={12} />}<TypeChip id={p} /></span>)}</span></td>
                  <td><span className={`status ${live(q.status) ? "info" : ""}`}>{STATUS_LABEL[q.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function TypesTab({ stats }: { stats?: OntologyStats | null }) {
  const [q, setQ] = useState("");
  const rows = OBJECTS.filter((o) => (o.name + o.what).toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <div className="btn-row" style={{ marginBottom: 12 }}>
        <input className="input" style={{ maxWidth: 320 }} placeholder="Filter object types" aria-label="Filter object types" value={q} onChange={(e) => setQ(e.target.value)} />
        <span className="muted" style={{ fontSize: 12 }}>Interfaces: {INTERFACES.map((i) => i.name).join(" · ")}</span>
      </div>
      <div className="dash-card" style={{ padding: 0 }}>
        <table className="dgrid">
          <thead><tr><th>Object type</th><th>Area</th><th>Data lives in</th><th style={{ textAlign: "right" }}>Properties</th><th style={{ textAlign: "right" }}>Links</th><th style={{ textAlign: "right" }}>Actions</th><th style={{ textAlign: "right" }}>Objects</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((o) => {
              const n = o.side === "courage" ? stats?.records.find((r) => r.type === o.id)?.count : stats?.links.find((l) => l.entity === o.id)?.distinctRecords;
              return (
                <tr key={o.id}>
                  <td><TypeChip id={o.id} /></td>
                  <td>{findArea(AREA_OF[o.id] ?? "")?.name ?? "—"}</td>
                  <td className="muted">{sourceOf(o.id).system}</td>
                  <td style={{ textAlign: "right" }}>{PROPS[o.id]?.length ?? 0}</td>
                  <td style={{ textAlign: "right" }}>{linksOf(o.id).length}</td>
                  <td style={{ textAlign: "right" }}>{ACTIONS.filter((a) => a.on.includes(o.id)).length}</td>
                  <td style={{ textAlign: "right" }}>{n ?? "—"}</td>
                  <td><span className={`status ${live(o.status) ? "info" : ""}`}>{STATUS_LABEL[o.status]}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function LinksTab() {
  return (
    <div className="dash-card" style={{ padding: 0 }}>
      <table className="dgrid">
        <thead><tr><th>From</th><th>Link</th><th>To</th><th>Cardinality</th><th>How it's made</th><th>Status</th></tr></thead>
        <tbody>
          {EDGES.map((e, i) => (
            <tr key={i}>
              <td><TypeChip id={e.from} /></td>
              <td style={{ fontStyle: "italic" }}>{e.label}</td>
              <td><TypeChip id={e.to} /></td>
              <td className="muted">{cardOf(e)}</td>
              <td className="muted" style={{ fontSize: 12 }}>{VIA_LABEL[e.via]}</td>
              <td><span className={`status ${live(e.status) ? "info" : ""}`}>{STATUS_LABEL[e.status]}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ActionCard({ a }: { a: (typeof ACTIONS)[number] }) {
  return (
    <div className={`dash-card action-card ${live(a.status) ? "" : "planned"}`}>
      <div className="action-h">
        <span className="ico"><Icon name="plus" size={14} /></span>
        <span className="name">{a.name}</span>
        <span className={`status ${live(a.status) ? "info" : ""}`}>{STATUS_LABEL[a.status]}</span>
      </div>
      <p className="desc">{a.what}</p>
      <dl className="action-dl">
        <dt>On</dt><dd className="chips">{a.on.map((o) => <TypeChip key={o} id={o} />)}</dd>
        <dt>Inputs</dt><dd>{a.params.map((p) => `${p.name}${p.required ? "" : " (optional)"}`).join(", ")}</dd>
        <dt>Changes</dt><dd>{a.writes.join(" · ")}</dd>
        {a.bcWriteback && <><dt>In BC</dt><dd><span className="tag bc">BC</span> {a.bcWriteback}</dd></>}
        {a.sideEffects && <><dt>Also</dt><dd>{a.sideEffects.join(" · ")}</dd></>}
        <dt>Who</dt><dd>{a.who}</dd>
        <dt>AI tool</dt><dd>{a.mcp ? <code className="mono">{a.mcp}</code> : <span className="muted">When it ships</span>}</dd>
      </dl>
      {a.try && <Link to={a.try} className="btn" style={{ alignSelf: "flex-start" }}>Run it</Link>}
    </div>
  );
}

function ActionsTab() {
  return (
    <>
      <p className="dash-scope" style={{ marginTop: 0, marginBottom: 12 }}>Every change to the ontology goes through an action type: the same rules, history and permissions whether a person clicks it in courageMFG or an agent calls it from Claude or Copilot.</p>
      <div className="app-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 22rem), 1fr))" }}>
        {ACTIONS.map((a) => <ActionCard key={a.id} a={a} />)}
      </div>
    </>
  );
}

function FunctionsTab() {
  return (
    <div className="dash-card" style={{ padding: 0 }}>
      <table className="dgrid">
        <thead><tr><th>Function</th><th>What it does</th><th>Takes</th><th>Returns</th><th>AI tool</th><th>Status</th></tr></thead>
        <tbody>
          {FUNCTIONS.map((f) => (
            <tr key={f.id}>
              <td style={{ fontWeight: 600 }}>{f.try ? <Link to={f.try}>{f.name}</Link> : f.name}</td>
              <td>{f.what}</td>
              <td className="muted">{f.inputs}</td>
              <td className="muted">{f.returns}</td>
              <td>{f.mcp ? <code className="mono">{f.mcp}</code> : "—"}</td>
              <td><span className={`status ${live(f.status) ? "info" : ""}`}>{STATUS_LABEL[f.status]}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TABS = [
  { id: "", name: "Overview" }, { id: "types", name: "Object types" }, { id: "links", name: "Link types" },
  { id: "actions", name: "Action types" }, { id: "functions", name: "Functions" },
];

export function OntologyPage() {
  const { tab = "" } = useParams();
  const { data: stats, error } = useLoad(() => api.ontologyStats());
  return (
    <>
      <CommandBar status="Ontology">
        <Link className="cmd" to="/o/lot/L-2291">Example object view</Link>
        <Link className="cmd" to="/settings/integrations">Connect Claude or Copilot</Link>
      </CommandBar>
      <div className="page">
        <nav className="crumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><span>/</span><Link to="/a/insights">Insights and AI</Link><span>/</span><span>Ontology</span></nav>
        <header className="area-head">
          <span className="area-ico"><Icon name="insights" size={22} /></span>
          <div>
            <h1 className="serif">Ontology</h1>
            <p className="sub">The operating model of the plant: every object courageMFG and Business Central hold, how they link, and the actions that change them, for people and AI agents alike.</p>
          </div>
        </header>
        <div className="pivot" role="tablist" style={{ marginBottom: 16 }}>
          {TABS.map((t) => (
            <NavLink key={t.id} end to={t.id ? `/ontology/${t.id}` : "/ontology"} role="tab" className={({ isActive }) => (isActive ? "active" : "")}>{t.name}</NavLink>
          ))}
        </div>
        <ErrorBox error={error} />
        {tab === "" && <Overview stats={stats} />}
        {tab === "types" && <TypesTab stats={stats} />}
        {tab === "links" && <LinksTab />}
        {tab === "actions" && <ActionsTab />}
        {tab === "functions" && <FunctionsTab />}
      </div>
    </>
  );
}
